import { useCallback, useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart,
  ClipboardText,
  Handshake,
  SignOut,
  House,
  User,
  MapPin,
  Calendar,
  ArrowLeft,
  Briefcase,
  GraduationCap,
  Heart as HeartIcon,
  Warning,
  CheckCircle,
  Phone,
  CurrencyDollar,
  Note,
  Plus,
  Bell,
  ArrowRight,
  Clock,
  Sparkle,
  Users,
  Key,
  PencilSimple,
  Trash
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: 'applied', label: 'Applied', color: 'bg-amber-500' },
  { key: 'accepted', label: 'Accepted', color: 'bg-blue-500' },
  { key: 'consultation_scheduled', label: 'Consultation Scheduled', color: 'bg-purple-500' },
  { key: 'consultation_done', label: 'Consultation Done', color: 'bg-indigo-500' },
  { key: 'paid', label: 'Paid', color: 'bg-emerald-500' },
  { key: 'profile_complete', label: 'Profile Complete', color: 'bg-teal-500' },
  { key: 'matching', label: 'Matching', color: 'bg-pink-500' },
  { key: 'matched', label: 'Matched', color: 'bg-rose-500' },
  { key: 'paused', label: 'Paused', color: 'bg-orange-500' },
  { key: 'archived', label: 'Archived', color: 'bg-stone-500' }
];

const PLANS = [
  { key: 'basic', label: 'Basic ($100)', amount: 100 },
  { key: 'standard', label: 'Standard ($250)', amount: 250 },
  { key: 'premium', label: 'Premium ($500)', amount: 500 }
];

// Display helpers for the full application questionnaire (Question → Answer format)
const QASection = ({ title, children }) => (
  <div className="mt-6 pt-6 border-t border-stone-700 first:mt-0 first:pt-0 first:border-0">
    <h3 className="text-pink-300 uppercase text-xs tracking-wider mb-4 font-medium">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

const QA = ({ question, answer, danger, success }) => {
  const isEmpty = answer === null || answer === undefined || answer === '' || answer === false;
  const display = Array.isArray(answer)
    ? answer.filter((a) => a && a.toString().trim()).join(', ')
    : typeof answer === 'boolean'
      ? (answer ? 'Yes' : 'No')
      : answer;
  return (
    <div className="bg-stone-700/30 rounded-md p-4 border-l-2 border-pink-500/50">
      <div className="text-pink-200 text-sm font-medium mb-2 leading-snug">
        Q: {question}
      </div>
      {isEmpty || (Array.isArray(answer) && display === '') ? (
        <div className="text-stone-500 italic text-sm">A: Not provided</div>
      ) : (
        <div
          className={`text-sm whitespace-pre-wrap break-words ${
            danger ? 'text-red-300' : success ? 'text-emerald-300' : 'text-white'
          }`}
        >
          <span className="text-stone-400">A: </span>
          {display}
        </div>
      )}
    </div>
  );
};

const AdminUserDetail = () => {
  const { userId } = useParams();
  const { user: adminUser, logout } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [stageHistory, setStageHistory] = useState([]);
  
  // Dialog states
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [showConsultDialog, setShowConsultDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Form states
  const [stageForm, setStageForm] = useState({ stage: '', notes: '' });
  const [consultForm, setConsultForm] = useState({ scheduled_date: '', notes: '' });
  const [paymentForm, setPaymentForm] = useState({ plan: 'basic', amount: 100, notes: '' });
  const [noteForm, setNoteForm] = useState('');
  const [reminderForm, setReminderForm] = useState({ title: '', description: '', due_date: '', priority: 'medium', reminder_type: 'follow_up' });
  const [newPassword, setNewPassword] = useState('');
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const fetchUserDetail = useCallback(async () => {
    try {
      const [userRes, notesRes, historyRes] = await Promise.all([
        axios.get(`${API}/admin/user/${userId}`),
        axios.get(`${API}/admin/user/${userId}/notes`),
        axios.get(`${API}/admin/user/${userId}/stage-history`)
      ]);
      setUserData(userRes.data);
      setNotes(notesRes.data);
      setStageHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to fetch user details');
      navigate('/admin/applications');
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchUserDetail();
  }, [fetchUserDetail]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleBanUser = async () => {
    if (!window.confirm('Are you sure you want to ban this user? They will no longer be able to log in.')) return;
    try {
      await axios.put(`${API}/admin/user/${userId}/ban`);
      toast.success('User banned');
      fetchUserDetail();
    } catch (error) {
      toast.error('Failed to ban user');
    }
  };

  const handleUnbanUser = async () => {
    try {
      await axios.put(`${API}/admin/user/${userId}/unban`);
      toast.success('User unbanned');
      fetchUserDetail();
    } catch (error) {
      toast.error('Failed to unban user');
    }
  };

  const openEditDialog = () => {
    setEditForm({
      name: user?.name || '',
      email: user?.email || '',
      age: user?.age || '',
      gender: user?.gender || '',
      location: user?.location || '',
      preferred_name: application?.preferred_name || '',
      phone: application?.phone || '',
      height: application?.height || '',
      occupation: application?.occupation || '',
      work_status: application?.work_status || '',
      relationship_goal: application?.relationship_goal || '',
      timeline: application?.timeline || '',
      age_range_min: application?.age_range_min || '',
      age_range_max: application?.age_range_max || '',
      must_have_qualities: application?.must_have_qualities || '',
      deal_breakers: application?.deal_breakers || '',
      faith_importance: application?.faith_importance || '',
      religious_affiliation: application?.religious_affiliation || '',
      anything_else: application?.anything_else || ''
    });
    setShowEditDialog(true);
  };

  const saveProfileEdits = async () => {
    try {
      // Strip empty strings, send only changed/filled values
      const payload = Object.fromEntries(
        Object.entries(editForm).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      // Coerce numeric fields
      ['age', 'age_range_min', 'age_range_max'].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = parseInt(payload[k], 10);
      });
      await axios.put(`${API}/admin/user/${userId}/profile`, payload);
      toast.success('Profile updated');
      setShowEditDialog(false);
      fetchUserDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const handleDeleteUser = async () => {
    if (deleteConfirm !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    try {
      await axios.delete(`${API}/admin/user/${userId}`);
      toast.success('User deleted permanently');
      setShowDeleteDialog(false);
      navigate('/admin/database');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const updateStage = async () => {
    try {
      await axios.put(`${API}/admin/user/${userId}/stage`, stageForm);
      toast.success(`Stage updated to ${stageForm.stage}`);
      setShowStageDialog(false);
      setStageForm({ stage: '', notes: '' });
      fetchUserDetail();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const scheduleConsultation = async () => {
    try {
      await axios.post(`${API}/admin/consultation/schedule`, {
        user_id: userId,
        scheduled_date: consultForm.scheduled_date,
        notes: consultForm.notes
      });
      toast.success('Consultation scheduled');
      setShowConsultDialog(false);
      setConsultForm({ scheduled_date: '', notes: '' });
      fetchUserDetail();
    } catch (error) {
      toast.error('Failed to schedule consultation');
    }
  };

  const recordPayment = async () => {
    try {
      await axios.post(`${API}/admin/payment/record`, {
        user_id: userId,
        amount: paymentForm.amount,
        plan: paymentForm.plan,
        payment_method: 'manual',
        notes: paymentForm.notes
      });
      toast.success('Payment recorded');
      setShowPaymentDialog(false);
      setPaymentForm({ plan: 'basic', amount: 100, notes: '' });
      fetchUserDetail();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const addNote = async () => {
    try {
      await axios.post(`${API}/admin/user/${userId}/note`, {
        user_id: userId,
        note: noteForm
      });
      toast.success('Note added');
      setShowNoteDialog(false);
      setNoteForm('');
      fetchUserDetail();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const createReminder = async () => {
    try {
      await axios.post(`${API}/admin/reminders`, {
        ...reminderForm,
        user_id: userId
      });
      toast.success('Reminder created');
      setShowReminderDialog(false);
      setReminderForm({ title: '', description: '', due_date: '', priority: 'medium', reminder_type: 'follow_up' });
    } catch (error) {
      toast.error('Failed to create reminder');
    }
  };

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/user/${userId}/reset-password`, {
        user_id: userId,
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${userData?.user?.name}`);
      setShowPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const getCurrentStage = () => {
    const stage = userData?.user?.journey_stage || userData?.user?.application_status;
    if (stage === 'submitted') return 'applied';
    return stage || 'not_submitted';
  };

  const getStageInfo = (stageKey) => {
    return STAGES.find(s => s.key === stageKey) || { label: stageKey, color: 'bg-gray-500' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="text-stone-400">Loading...</div>
      </div>
    );
  }

  const { user, application, matches } = userData || {};
  const currentStage = getCurrentStage();
  const currentStageInfo = getStageInfo(currentStage);

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Heart weight="fill" className="w-6 h-6 text-primary" />
              <span className="font-serif text-xl text-white">Admin Dashboard</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-stone-400 text-sm">Welcome, {adminUser?.name}</span>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-400 hover:text-white">
                <SignOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <House className="w-4 h-4 inline mr-2" />Overview
            </Link>
            <Link to="/admin/applications" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <ClipboardText className="w-4 h-4 inline mr-2" />Applications
            </Link>
            <Link to="/admin/database" className="py-4 text-white border-b-2 border-primary text-sm font-medium">
              <Users className="w-4 h-4 inline mr-2" />User Database
            </Link>
            <Link to="/admin/matching" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium">
              <Handshake className="w-4 h-4 inline mr-2" />Matching
            </Link>
            <Link to="/admin/team" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              Admin Team
            </Link>
            <Link to="/admin/account" className="py-4 text-stone-400 hover:text-white border-b-2 border-transparent text-sm font-medium whitespace-nowrap">
              My Account
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Back Button */}
        <Link to="/admin/database" className="inline-flex items-center gap-2 text-stone-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to User Database
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* User Header Card */}
            <div className="bg-stone-800 rounded-lg p-6">
              <div className="flex items-start gap-6">
                <div className="w-24 h-24 rounded-lg bg-stone-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {application?.photo_base64 ? (
                    <img src={application.photo_base64} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User weight="light" className="w-12 h-12 text-stone-500" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="font-serif text-2xl text-white" data-testid="user-detail-name">
                      {user?.name}
                    </h1>
                    <Badge className={`${currentStageInfo.color} text-white`}>
                      {currentStageInfo.label}
                    </Badge>
                    {user?.plan && (
                      <Badge className="bg-primary/20 text-primary border-0">
                        {user.plan.charAt(0).toUpperCase() + user.plan.slice(1)} Plan
                      </Badge>
                    )}
                    {user?.banned && (
                      <Badge className="bg-red-600 text-white border-0" data-testid="banned-badge">
                        Banned
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-stone-400 text-sm mb-3">
                    <span>{user?.age} years • {user?.gender}</span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {user?.location}
                    </span>
                  </div>
                  <div className="text-stone-500 text-xs">
                    <span>Email: {user?.email}</span>
                    <span className="mx-2">•</span>
                    <span>Joined: {new Date(user?.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stage Management Card */}
            <div className="bg-stone-800 rounded-lg p-6" data-testid="stage-management">
              <h2 className="font-serif text-xl text-white mb-4 flex items-center gap-2">
                <Sparkle className="w-5 h-5 text-primary" />
                Journey Management
              </h2>
              
              {/* Quick Stage Actions */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Button
                  onClick={() => { setStageForm({ stage: 'accepted', notes: '' }); setShowStageDialog(true); }}
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-900/30"
                  disabled={currentStage === 'accepted'}
                  data-testid="accept-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Accept
                </Button>
                <Button
                  onClick={() => setShowConsultDialog(true)}
                  variant="outline"
                  className="border-purple-500/50 text-purple-400 hover:bg-purple-900/30"
                  data-testid="schedule-consult-btn"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Schedule Consult
                </Button>
                <Button
                  onClick={() => setShowPaymentDialog(true)}
                  variant="outline"
                  className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/30"
                  data-testid="record-payment-btn"
                >
                  <CurrencyDollar className="w-4 h-4 mr-2" />
                  Record Payment
                </Button>
                <Button
                  onClick={() => setShowStageDialog(true)}
                  variant="outline"
                  className="border-stone-500 text-stone-300 hover:bg-stone-700"
                  data-testid="change-stage-btn"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Change Stage
                </Button>
              </div>

              {/* Stage History */}
              {stageHistory.length > 0 && (
                <div>
                  <h3 className="text-stone-400 text-sm mb-3">Stage History</h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {stageHistory.map((h) => (
                      <div key={h.id} className="flex items-center gap-3 text-sm bg-stone-700/30 rounded p-2">
                        <div className={`w-2 h-2 rounded-full ${getStageInfo(h.stage).color}`} />
                        <span className="text-white">{getStageInfo(h.stage).label}</span>
                        {h.notes && <span className="text-stone-500">- {h.notes}</span>}
                        <span className="text-stone-500 ml-auto text-xs">
                          {new Date(h.changed_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Application Details - Full Questionnaire (Q&A format) */}
            <div className="bg-stone-800 rounded-lg p-6" data-testid="application-details">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-serif text-xl text-white">Application Questionnaire</h2>
                  <p className="text-stone-500 text-xs mt-1">Every question asked on the intake form, with the client's answer</p>
                </div>
                {(() => {
                  if (!application) {
                    return (
                      <Badge className="bg-stone-600 text-white">Not Started</Badge>
                    );
                  }
                  if (application.is_draft) {
                    return (
                      <Badge className="bg-amber-600 text-white" data-testid="status-in-progress">
                        In Progress (Draft)
                      </Badge>
                    );
                  }
                  return (
                    <Badge className="bg-emerald-600 text-white" data-testid="status-submitted">
                      Submitted
                    </Badge>
                  );
                })()}
              </div>

              {/* Status meta line */}
              <div className="flex flex-wrap gap-4 text-xs text-stone-500 mb-4 pb-4 border-b border-stone-700">
                {application?.started_at && (
                  <span>Started: {new Date(application.started_at).toLocaleString()}</span>
                )}
                {application?.last_draft_at && application?.is_draft && (
                  <span>Last edited: {new Date(application.last_draft_at).toLocaleString()}</span>
                )}
                {application?.submitted_at && !application?.is_draft && (
                  <span>Submitted: {new Date(application.submitted_at).toLocaleString()}</span>
                )}
                {!application && (
                  <span className="italic">This client has not opened the application form yet.</span>
                )}
              </div>

              {application ? (
                <>
                  {/* STEP 1 — Basic Information */}
                  <QASection title="Step 1 — Basic Information">
                    {application.photo_base64 && (
                      <div className="bg-stone-700/30 rounded-md p-4 border-l-2 border-pink-500/50">
                        <div className="text-pink-200 text-sm font-medium mb-2">Q: Profile Photo (at least 2 clear, up-to-date pictures — face & full body)</div>
                        <img src={application.photo_base64} alt="" className="w-32 h-32 object-cover rounded-lg border border-stone-600" />
                      </div>
                    )}
                    <QA question="Preferred Name / Nickname — What should we call you?" answer={application.preferred_name} />
                    <QA question="Phone Number" answer={application.phone} />
                    <QA question="Height" answer={application.height} />
                  </QASection>

                  {/* Phone Interview info banner */}
                  <div className="mt-6 pt-6 border-t border-stone-700">
                    <div className="bg-amber-900/20 border border-amber-700/40 rounded-lg p-4 text-amber-200 text-sm" data-testid="phone-interview-banner">
                      <strong>Phone Interview Required:</strong> Relationship intentions, ideal match preferences,
                      lifestyle, values and readiness are gathered by phone after this application is submitted.
                      Use the <em>Admin Notes</em> sidebar to record interview answers.
                    </div>
                  </div>

                  {/* LEGACY: Previously-collected questionnaire data (only shown if data exists) */}
                  {(application.relationship_type?.length || application.relationship_type_other || application.timeline ||
                    application.personality_traits?.length || application.personality_traits_other ||
                    application.free_time_activities || application.three_qualities?.some((q) => q)) && (
                    <QASection title="Legacy — Relationship Intentions (older submissions)">
                      <QA
                        question="What are you looking for? (Select all that apply)"
                        answer={
                          [
                            ...(Array.isArray(application.relationship_type) ? application.relationship_type : []),
                            application.relationship_type_other ? `Other: ${application.relationship_type_other}` : null
                          ].filter(Boolean)
                        }
                      />
                      <QA question="Timeline — How soon are you hoping to meet someone compatible?" answer={application.timeline} />
                      <QA
                        question="How would you describe your personality? (Select all that apply)"
                        answer={
                          [
                            ...(Array.isArray(application.personality_traits) ? application.personality_traits : []),
                            application.personality_traits_other ? `Other: ${application.personality_traits_other}` : null
                          ].filter(Boolean)
                        }
                      />
                      <QA question="Free Time — What do you enjoy doing in your free time?" answer={application.free_time_activities} />
                      <QA
                        question="Three Qualities You Bring — What are three qualities you bring into a relationship?"
                        answer={Array.isArray(application.three_qualities) ? application.three_qualities.filter((q) => q) : null}
                      />
                    </QASection>
                  )}

                  {/* LEGACY: Ideal Match (only if data exists) */}
                  {(application.preferred_genders?.length || application.preferred_gender_other ||
                    application.age_range_min || application.age_range_max || application.location_preference ||
                    application.location_preference_other) && (
                    <QASection title="Legacy — Your Ideal Match (older submissions)">
                      <QA
                        question="Preferred Gender"
                        answer={
                          [
                            ...(Array.isArray(application.preferred_genders) ? application.preferred_genders : []),
                            application.preferred_gender_other ? `Other: ${application.preferred_gender_other}` : null
                          ].filter(Boolean)
                        }
                      />
                      <QA
                        question="Preferred Age Range"
                        answer={
                          application.age_range_min || application.age_range_max
                            ? `${application.age_range_min || '?'} – ${application.age_range_max || '?'}`
                            : null
                        }
                      />
                      <QA
                        question="Are you open to dating someone:"
                        answer={
                          application.location_preference_other
                            ? `${application.location_preference || ''}${application.location_preference ? ' • ' : ''}Other: ${application.location_preference_other}`
                            : application.location_preference
                        }
                      />
                    </QASection>
                  )}

                  {/* LEGACY: Lifestyle (only if data exists) */}
                  {application.lifestyle_preferences && (
                    <QASection title="Legacy — Lifestyle Preferences (older submissions)">
                      <QA
                        question="Any lifestyle preferences or boundaries we should know?"
                        answer={application.lifestyle_preferences}
                      />
                    </QASection>
                  )}

                  {/* LEGACY: Readiness (only if data exists) */}
                  {(application.successful_match_definition || application.emotionally_available ||
                    application.open_to_feedback || application.anything_else) && (
                    <QASection title="Legacy — Readiness & Expectations (older submissions)">
                      <QA question="Your Vision of Success — What does a successful match look like to you?" answer={application.successful_match_definition} />
                      <QA question="Emotional Readiness — Are you emotionally available and ready to date intentionally?" answer={application.emotionally_available} />
                      <QA question="Openness to Feedback — Are you open to feedback and guidance during the matchmaking process?" answer={application.open_to_feedback} />
                      <QA question="Anything Else? — Is there anything about you that a matchmaker should know to match you well?" answer={application.anything_else} />
                    </QASection>
                  )}

                  {/* Step 2 (renamed from Step 6) — Client Service Agreement */}
                  <QASection title="Step 2 — Client Service Agreement & E-Signature">
                    <QA
                      question="I have read, understood and agree to ALL terms of this Client Service Agreement"
                      answer={application.agree_confidentiality}
                      success={application.agree_confidentiality}
                      danger={!application.agree_confidentiality}
                    />
                    <QA
                      question="I understand that fees are NON-REFUNDABLE and Link, Laugh & Love does not guarantee matches, relationships, compatibility, marriage or emotional connection"
                      answer={application.agree_disclaimer}
                      success={application.agree_disclaimer}
                      danger={!application.agree_disclaimer}
                    />
                    <QA
                      question="I confirm that I am 21 years of age or older"
                      answer={application.agree_non_discrimination}
                      success={application.agree_non_discrimination}
                      danger={!application.agree_non_discrimination}
                    />
                    <QA question="Client Name (Print) / Electronic Signature" answer={application.signature_name} />
                  </QASection>
                </>
              ) : (
                <div className="text-stone-500 text-sm text-center py-8">
                  This client has signed up but has not opened the application form yet. Once they start answering, every response will appear here in real time.
                </div>
              )}
            </div>

            {/* Match History */}
            <div className="bg-stone-800 rounded-lg p-6">
              <h2 className="font-serif text-xl text-white mb-4">Match History</h2>
              {matches?.length === 0 ? (
                <p className="text-stone-500 text-center py-4">No matches yet</p>
              ) : (
                <div className="space-y-3">
                  {matches?.map((match) => (
                    <div key={match.id} className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <HeartIcon weight="fill" className="w-5 h-5 text-primary" />
                        <span className="text-white">Match #{match.id.slice(0, 8)}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge className={
                          match.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                          match.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-amber-500/20 text-amber-400'
                        }>
                          {match.status}
                        </Badge>
                        <span className="text-stone-400 text-sm">
                          {new Date(match.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-stone-800 rounded-lg p-6">
              <h2 className="font-serif text-lg text-white mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Button onClick={openEditDialog} variant="outline" className="w-full justify-start border-stone-700 text-stone-300 hover:bg-stone-700" data-testid="edit-profile-btn">
                  <PencilSimple className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
                <Button onClick={() => setShowNoteDialog(true)} variant="outline" className="w-full justify-start border-stone-700 text-stone-300">
                  <Note className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
                <Button onClick={() => setShowReminderDialog(true)} variant="outline" className="w-full justify-start border-stone-700 text-stone-300">
                  <Bell className="w-4 h-4 mr-2" />
                  Set Reminder
                </Button>
                <Button onClick={() => setShowPasswordDialog(true)} variant="outline" className="w-full justify-start border-stone-700 text-stone-300" data-testid="reset-password-btn">
                  <Key className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
                <Link to={`/admin/matching?user=${userId}`} className="block">
                  <Button className="w-full justify-start btn-primary">
                    <Handshake className="w-4 h-4 mr-2" />
                    Find Match
                  </Button>
                </Link>
                {user?.banned ? (
                  <Button onClick={handleUnbanUser} variant="outline" className="w-full justify-start border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/30" data-testid="unban-user-btn">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Unban User
                  </Button>
                ) : (
                  <Button variant="outline" onClick={handleBanUser} className="w-full justify-start border-amber-500/50 text-amber-400 hover:bg-amber-900/30" data-testid="ban-user-btn">
                    <Warning className="w-4 h-4 mr-2" />
                    Ban User
                  </Button>
                )}
                <Button variant="destructive" onClick={() => { setDeleteConfirm(''); setShowDeleteDialog(true); }} className="w-full justify-start" data-testid="delete-user-btn">
                  <Trash className="w-4 h-4 mr-2" />
                  Delete Profile
                </Button>
              </div>
            </div>

            {/* Admin Notes */}
            <div className="bg-stone-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-lg text-white">Admin Notes</h2>
                <Button size="sm" variant="ghost" onClick={() => setShowNoteDialog(true)} className="text-primary">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {notes.length === 0 ? (
                <p className="text-stone-500 text-sm text-center py-4">No notes yet</p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {notes.map((note) => (
                    <div key={note.id} className="bg-stone-700/30 rounded p-3">
                      <p className="text-stone-300 text-sm">{note.note}</p>
                      <p className="text-stone-500 text-xs mt-2">
                        {new Date(note.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Change Stage Dialog */}
      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Update User Stage</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-stone-400 text-sm mb-1 block">New Stage</label>
              <Select value={stageForm.stage} onValueChange={(v) => setStageForm(prev => ({ ...prev, stage: v }))}>
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Notes (optional)</label>
              <Textarea
                placeholder="Add any notes about this stage change..."
                value={stageForm.notes}
                onChange={(e) => setStageForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowStageDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={updateStage} className="btn-primary" disabled={!stageForm.stage}>Update Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Consultation Dialog */}
      <Dialog open={showConsultDialog} onOpenChange={setShowConsultDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Schedule Consultation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Date & Time</label>
              <Input
                type="datetime-local"
                value={consultForm.scheduled_date}
                onChange={(e) => setConsultForm(prev => ({ ...prev, scheduled_date: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Notes</label>
              <Textarea
                placeholder="Meeting details, phone number to call, etc..."
                value={consultForm.notes}
                onChange={(e) => setConsultForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConsultDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={scheduleConsultation} className="btn-primary" disabled={!consultForm.scheduled_date}>
              <Calendar className="w-4 h-4 mr-2" />
              Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Plan</label>
              <Select 
                value={paymentForm.plan} 
                onValueChange={(v) => {
                  const plan = PLANS.find(p => p.key === v);
                  setPaymentForm(prev => ({ ...prev, plan: v, amount: plan?.amount || 0 }));
                }}
              >
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLANS.map((p) => (
                    <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Amount ($)</label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: parseFloat(e.target.value) }))}
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Notes</label>
              <Textarea
                placeholder="Payment method, transaction ID, etc..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowPaymentDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={recordPayment} className="btn-primary">
              <CurrencyDollar className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Admin Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Write a private note about this user..."
              value={noteForm}
              onChange={(e) => setNoteForm(e.target.value)}
              className="bg-stone-700 border-stone-600 text-white"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowNoteDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={addNote} className="btn-primary" disabled={!noteForm.trim()}>
              <Note className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Set Reminder for {user?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Title *</label>
              <Input
                placeholder="e.g., Follow up with Sarah"
                value={reminderForm.title}
                onChange={(e) => setReminderForm(prev => ({ ...prev, title: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Description</label>
              <Textarea
                placeholder="Additional details..."
                value={reminderForm.description}
                onChange={(e) => setReminderForm(prev => ({ ...prev, description: e.target.value }))}
                className="bg-stone-700 border-stone-600 text-white"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-stone-400 text-sm mb-1 block">Due Date</label>
                <Input
                  type="datetime-local"
                  value={reminderForm.due_date}
                  onChange={(e) => setReminderForm(prev => ({ ...prev, due_date: e.target.value }))}
                  className="bg-stone-700 border-stone-600 text-white"
                />
              </div>
              <div>
                <label className="text-stone-400 text-sm mb-1 block">Priority</label>
                <Select value={reminderForm.priority} onValueChange={(v) => setReminderForm(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReminderDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={createReminder} className="btn-primary" disabled={!reminderForm.title}>
              <Bell className="w-4 h-4 mr-2" />
              Create Reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>        <DialogContent className="bg-stone-800 border-stone-700 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Reset Password for {user?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-stone-400 text-sm mb-4">
              Enter a new password for <strong>{user?.email}</strong>. The user will be able to log in with this new password immediately.
            </p>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">New Password</label>
              <Input
                type="password"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-stone-700 border-stone-600 text-white"
                data-testid="new-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowPasswordDialog(false); setNewPassword(''); }} className="text-stone-400">Cancel</Button>
            <Button onClick={resetPassword} className="btn-primary" disabled={newPassword.length < 6} data-testid="confirm-reset-password-btn">
              <Key className="w-4 h-4 mr-2" />
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Edit Profile — {user?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <h3 className="text-stone-400 uppercase text-xs tracking-wider mb-3">Core Info</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Name</label>
                  <Input value={editForm.name || ''} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" data-testid="edit-name-input" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Email</label>
                  <Input value={editForm.email || ''} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" data-testid="edit-email-input" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Age</label>
                  <Input type="number" value={editForm.age || ''} onChange={(e) => setEditForm(p => ({ ...p, age: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Gender</label>
                  <Select value={editForm.gender || ''} onValueChange={(v) => setEditForm(p => ({ ...p, gender: v }))}>
                    <SelectTrigger className="bg-stone-700 border-stone-600 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="non-binary">Non-binary</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <label className="text-stone-400 text-sm mb-1 block">Location</label>
                  <Input value={editForm.location || ''} onChange={(e) => setEditForm(p => ({ ...p, location: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-stone-400 uppercase text-xs tracking-wider mb-3">Contact & Background</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Preferred Name</label>
                  <Input value={editForm.preferred_name || ''} onChange={(e) => setEditForm(p => ({ ...p, preferred_name: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Phone</label>
                  <Input value={editForm.phone || ''} onChange={(e) => setEditForm(p => ({ ...p, phone: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Height</label>
                  <Input value={editForm.height || ''} onChange={(e) => setEditForm(p => ({ ...p, height: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Occupation</label>
                  <Input value={editForm.occupation || ''} onChange={(e) => setEditForm(p => ({ ...p, occupation: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Work Status</label>
                  <Input value={editForm.work_status || ''} onChange={(e) => setEditForm(p => ({ ...p, work_status: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Relationship Goal</label>
                  <Input value={editForm.relationship_goal || ''} onChange={(e) => setEditForm(p => ({ ...p, relationship_goal: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Timeline</label>
                  <Input value={editForm.timeline || ''} onChange={(e) => setEditForm(p => ({ ...p, timeline: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-stone-400 text-sm mb-1 block">Age Range Min</label>
                    <Input type="number" value={editForm.age_range_min || ''} onChange={(e) => setEditForm(p => ({ ...p, age_range_min: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                  </div>
                  <div>
                    <label className="text-stone-400 text-sm mb-1 block">Age Range Max</label>
                    <Input type="number" value={editForm.age_range_max || ''} onChange={(e) => setEditForm(p => ({ ...p, age_range_max: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-stone-400 uppercase text-xs tracking-wider mb-3">Preferences & Notes</h3>
              <div className="space-y-3">
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Must-Have Qualities</label>
                  <Textarea value={editForm.must_have_qualities || ''} onChange={(e) => setEditForm(p => ({ ...p, must_have_qualities: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" rows={2} />
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Deal Breakers</label>
                  <Textarea value={editForm.deal_breakers || ''} onChange={(e) => setEditForm(p => ({ ...p, deal_breakers: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-stone-400 text-sm mb-1 block">Faith Importance</label>
                    <Input value={editForm.faith_importance || ''} onChange={(e) => setEditForm(p => ({ ...p, faith_importance: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                  </div>
                  <div>
                    <label className="text-stone-400 text-sm mb-1 block">Religious Affiliation</label>
                    <Input value={editForm.religious_affiliation || ''} onChange={(e) => setEditForm(p => ({ ...p, religious_affiliation: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" />
                  </div>
                </div>
                <div>
                  <label className="text-stone-400 text-sm mb-1 block">Anything Else</label>
                  <Textarea value={editForm.anything_else || ''} onChange={(e) => setEditForm(p => ({ ...p, anything_else: e.target.value }))} className="bg-stone-700 border-stone-600 text-white" rows={2} />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditDialog(false)} className="text-stone-400">Cancel</Button>
            <Button onClick={saveProfileEdits} className="btn-primary" data-testid="save-profile-btn">
              <PencilSimple className="w-4 h-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-stone-800 border-red-900/50 text-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-red-400 flex items-center gap-2">
              <Warning weight="fill" className="w-5 h-5" />
              Permanently Delete Profile
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-900/20 border border-red-900/50 rounded-lg p-4">
              <p className="text-red-200 text-sm">
                You are about to <strong>permanently delete</strong> {user?.name} ({user?.email}).
                This will remove their account, application, notes, matches, payments, reminders, and all related data.
                <strong> This action cannot be undone.</strong>
              </p>
            </div>
            <p className="text-stone-400 text-sm">If you only want to stop matching them, use <em>Ban User</em> or set status to <em>Archived</em> instead.</p>
            <div>
              <label className="text-stone-400 text-sm mb-1 block">Type <span className="text-red-400 font-mono">DELETE</span> to confirm</label>
              <Input
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                className="bg-stone-700 border-stone-600 text-white"
                data-testid="delete-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteDialog(false)} className="text-stone-400">Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deleteConfirm !== 'DELETE'} data-testid="confirm-delete-user-btn">
              <Trash className="w-4 h-4 mr-2" />
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUserDetail;
